import classNames from 'classnames';
import { Zap } from 'lucide-react';
import { useContext, useState, useEffect } from 'react';
import dayjs from 'dayjs';

import { AppCtx } from 'pages/_app';
import { generateFormattedDeliveryDates } from '@/components/v2/product/hooks/helpers';
import { useProductPageContext } from '@/components/v2/product/hooks/useProductPageContext';
import i18n from '@/settings/i18n';

const t = i18n.init('product');

// Colours the "free" word of the localized label green. It's the all-caps word
// in every locale ("FREE Delivery", "Livraison GRATUITE", "KOSTENLOSE
// Lieferung"), which is sturdier than matching a separate "Free" translation
// (Spanish has "Gratis" for that but "GRATUITA" here).
const highlightFree = (label: string) => {
  const match = label.match(/\p{Lu}{2,}/u);

  if (!match || match.index == null) {
    return label;
  }

  const end = match.index + match[0].length;

  return (
    <>
      {label.slice(0, match.index)}
      <span className="font-medium text-[#0ca764]">{match[0]}</span>
      {label.slice(end)}
    </>
  );
};

type PricingBoxDeliveryProps = {
  align?: 'left' | 'right' | 'center';
  className?: string;
};

const useCutoffCountdown = (cutoffDeadline: string | null): string | null => {
  const [countdown, setCountdown] = useState<string | null>(null);

  useEffect(() => {
    if (!cutoffDeadline) {
      setCountdown(null);

      return undefined;
    }

    const computeCountdown = () => {
      // cutoffDeadline is an absolute ISO instant (with offset) the server has
      // already adjusted for weekends/holidays, so we just count down to it —
      // no client-side timezone or business-day logic needed.
      const deadline = dayjs(cutoffDeadline);
      const now = dayjs();
      const diffMs = deadline.diff(now);

      // Only show urgency while the cutoff is still today from the customer's
      // point of view. If it has rolled to a future day (seller closed today,
      // e.g. a public holiday) or already passed, hide the timer entirely.
      if (diffMs < 0 || !deadline.isSame(now, 'day')) {
        setCountdown(null);

        return;
      }

      const totalMinutes = Math.floor(diffMs / 60_000);
      const hrs = Math.floor(totalMinutes / 60);
      const mins = totalMinutes % 60;
      const hours = `${hrs} ${hrs !== 1 ? t`hrs` : t`hr`}`;
      const minutes = `${mins} ${mins !== 1 ? t`mins` : t`min`}`;

      if (hrs > 0 && mins > 0) {
        setCountdown(t`${{ hours }}, ${{ minutes }}`);
      } else if (hrs > 0) {
        setCountdown(hours);
      } else {
        setCountdown(minutes);
      }
    };

    computeCountdown();
    const interval = setInterval(computeCountdown, 60_000);

    return () => clearInterval(interval);
  }, [cutoffDeadline]);

  return countdown;
};

export default function PricingBoxDelivery({
  align = 'right',
  className,
}: PricingBoxDeliveryProps) {
  const { zipcodeStateData } = useContext(AppCtx);
  const { selectedOffer, vendorDetails } = useProductPageContext();

  // Same estimation (offer estimates + destination) the cart uses, so dates
  // match. Read-only: express is only chosen in the cart, so no price here.
  const {
    formattedStandardDelivery,
    formattedExpressDelivery,
    standardCutoffDeadline,
  } = generateFormattedDeliveryDates({
    deliveryEstimates: selectedOffer?.deliveryEstimates,
    format: 'short',
    destinationState: zipcodeStateData?.state,
    destinationRegion: zipcodeStateData?.region,
  });

  const countdown = useCutoffCountdown(standardCutoffDeadline);

  const expressDelivery =
    vendorDetails.expressShipping && formattedExpressDelivery
      ? formattedExpressDelivery
      : null;

  let justifyClass = 'text-left';

  if (align === 'right') {
    justifyClass = 'justify-end text-right';
  } else if (align === 'center') {
    justifyClass = 'justify-center text-center';
  }

  return (
    <div
      className={classNames(
        'flex flex-col gap-1 text-[11px] font-normal leading-tight text-gray-700',
        align === 'right' && 'items-end',
        align === 'center' && 'items-center',
        className,
      )}
      id="e2e-pdp-expected-delivery-information"
    >
      <div
        className={classNames(
          'flex flex-wrap items-center gap-x-1.5 gap-y-0',
          justifyClass,
        )}
      >
        {/* Each label stays with its date; they only wrap apart when the line
            can't fit (e.g. at 200% text zoom), so nothing overflows. */}
        <div
          className={classNames(
            'flex min-h-3.5 flex-wrap items-center gap-x-1.5',
            justifyClass,
          )}
        >
          <span className="whitespace-nowrap">
            {highlightFree(t`FREE Delivery`)}
            {formattedStandardDelivery && ` ${formattedStandardDelivery}`}
          </span>
          {expressDelivery && (
            <span className="flex items-center gap-1.5 whitespace-nowrap">
              <span>-</span>
              <span className="flex items-center gap-0.5">
                <Zap className="h-3.5 w-3.5 shrink-0" />
                <span className="font-medium">{t`Express`}</span>{' '}
                {expressDelivery}
              </span>
            </span>
          )}
        </div>
        {countdown && (
          <>
            <div className="hidden sm:block">-</div>
            <div className="font-medium">
              {t`Order within`}{' '}
              <span className="text-[#E2590A]">{countdown}</span>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
